'use client';
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { styled } from '@mui/material/styles';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Search } from '../Search';
import { Highlight } from './Highlight';
import { SelectDropdownHeader } from './SelectDropdownHeader';
import { SelectItemIndicator } from './SelectItemIndicator';
import { SelectList } from './SelectList';
import { SelectListItem } from './SelectListItem';
import { handleSearchClear, handleSelectDropdownOpen } from './utils/a11y';
import { getLoadingStatusView } from './utils/getLoadingStatusView';
const StyledDiv = styled('div')(({ theme }) => ({
    borderRadius: theme.tokens.FuelBorderRadiusMd,
    boxShadow: '0px 1px 10px rgba(27, 27, 27, 0.20)',
    backgroundColor: theme.tokens.FuelColorWhite,
    color: theme.tokens.FuelColorFocused,
    maxHeight: '325px',
    overflowY: 'auto'
}));
export const SelectDropdown = forwardRef(({ hasDivider, hasSearch, hasSelectAll, id, multiple, loadingStatus, onOptionClick, options, selected, ...props }, ref) => {
    const [searchValue, setSearchValue] = useState('');
    const handleChange = (value, checked) => {
        if (!multiple) {
            onOptionClick([value]);
            setSearchValue('');
        }
        else if (checked) {
            onOptionClick([...selected, value]);
        }
        else {
            onOptionClick(selected.filter((item) => item !== value));
        }
    };
    const filteredOptions = (filterOptions) => {
        return filterOptions.filter((option) => option.children?.toString().toLowerCase().includes(searchValue.toLowerCase()));
    };
    const selectAllCheckedState = selected.length && selected.length < options.length ? 'mixed' : selected.length === options.length;
    const indicatorType = multiple ? 'checkbox' : 'radio';
    const rootRef = useRef(null);
    const searchRef = useRef(null);
    useImperativeHandle(ref, () => rootRef.current);
    useEffect(() => {
        if (rootRef.current) {
            handleSelectDropdownOpen(rootRef.current);
        }
    }, [rootRef, multiple]);
    const showSearch = hasSearch;
    const showSelectAll = hasSelectAll && multiple;
    const showDropdownHeader = showSearch || showSelectAll;
    return (_jsxs(StyledDiv, { className: 'FuelSelect-dropdown', "data-multiselect": multiple, id: id, ref: rootRef, role: 'presentation', ...props, children: [showDropdownHeader && (_jsxs(SelectDropdownHeader, { children: [showSearch && (_jsx(Search, { autoComplete: 'off', hiddenLabel: true, id: 'searchInput', inputRef: searchRef, label: 'Search for an item', onChange: (event) => {
                            setSearchValue(event.currentTarget.value);
                        }, onClear: () => {
                            setSearchValue('');
                            handleSearchClear(searchRef.current);
                        }, placeholder: 'Search' })), showSelectAll && (_jsxs(SelectListItem, { "aria-checked": selectAllCheckedState, "aria-labelledby": `${id}-selectAll-label`, disabled: loadingStatus !== undefined, id: `${id}-selectAll`, isCondensed: true, onClick: () => {
                            if (selected.length) {
                                onOptionClick([]);
                            }
                            else {
                                onOptionClick(options.map((option) => option.value));
                            }
                        }, role: 'option', children: [_jsx(SelectItemIndicator, { checked: selectAllCheckedState, type: 'checkbox' }), _jsx("span", { id: `${id}-selectAll-label`, children: selected.length ? 'Deselect All' : 'Select All' })] }))] })), getLoadingStatusView(loadingStatus) ?? (_jsx(SelectList, { "aria-label": `Select ${multiple ? 'items' : 'an item'}`, "aria-multiselectable": multiple, id: `${id}-listbox`, role: 'listbox', children: filteredOptions(options).map((option) => {
                    const optionId = `${id}-option-${option.index}`;
                    const isChecked = selected.includes(option.value);
                    const checkedProp = {
                        [`aria-${multiple ? 'checked' : 'selected'}`]: isChecked
                    };
                    return (_createElement(SelectListItem, { ...checkedProp, "aria-labelledby": `${optionId}-label`, "aria-posinset": option.index, "aria-setsize": options.length, checked: isChecked, disabled: option.disabled, hasDivider: hasDivider, id: optionId, key: optionId, onClick: (event) => {
                            handleChange(option.value, !isChecked);
                            option.onClick?.(event);
                        }, role: 'option' },
                        _jsx(SelectItemIndicator, { checked: isChecked, type: indicatorType }),
                        option.startIcon,
                        _jsx(Highlight, { id: `${optionId}-label`, inputValue: searchValue, label: option.children })));
                }) }))] }));
});
SelectDropdown.displayName = 'SelectDropdown';
